import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Chip } from "@mui/material";
import {
  FormControl,
  type FormControlProps,
  InputLabel,
  MenuItem,
} from "@mui/material";
import Select, { type SelectProps } from "@mui/material/Select";

import { Option } from "api/common/types/common.types";
import { useFieldContext } from "components/Forms/AppForm";

import ErrorMessage2 from "../../helpers/ErrorMessage2";

import { useNormalizedOptions } from "./UseNormalizedOptions";

type MultiSelectFieldProps = SelectProps<string[]> & {
  options: Option[] | string[];
  label: string;
  containerProps?: FormControlProps;
};

const MultiSelect = ({
  options,
  label,
  containerProps,
  required,
  ...props
}: MultiSelectFieldProps) => {
  const field = useFieldContext<string[]>();
  const normalizedOptions = useNormalizedOptions(options);

  return (
    <FormControl fullWidth size="small" {...containerProps} required={required}>
      <InputLabel
        id={`${label}-select-label`}
        error={field.state.meta.errors.length > 0}
      >
        {label}
      </InputLabel>
      <Select
        labelId={`${label}-select-label`}
        id={`${label}-select`}
        value={field.state.value}
        error={field.state.meta.errors.length > 0}
        label={label}
        onChange={(e) => {
          field.handleChange([...e.target.value]);
        }}
        {...props}
        multiple
        renderValue={(selected) => (
          <Box display="flex" flexDirection="column">
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  onDelete={() => {
                    field.handleChange(selected.filter((opt) => opt !== value));
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                />
              ))}
            </Box>

            <Button
              startIcon={<DeleteIcon />}
              variant="outlined"
              color="error"
              fullWidth
              sx={{
                marginTop: "1em",
                marginLeft: "-2px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              size="small"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                field.handleChange([]);
              }}
            >
              Clear All
            </Button>
          </Box>
        )}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          sx: {
            maxHeight: "500px",
          },
        }}
        sx={{
          ".MuiSelect-icon": {
            height: "100%",
            top: 0,
            borderLeft: "solid 1px rgba(0,0,0,0.1)",
            marginRight: "-6px",
          },
        }}
      >
        {normalizedOptions.map((opt) => (
          <MenuItem
            value={opt.value}
            key={opt.value}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "#ebebeb",
              },
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      <ErrorMessage2 />
    </FormControl>
  );
};
export default MultiSelect;
